import dayjs from 'dayjs';
import {
  AiCallSummaryCard,
  AiCallSummaryDirection,
  AiCallSummaryRow,
  AiCallSummaryStatus,
} from './ai-call-summary.types';

const STATUSES: AiCallSummaryStatus[] = [
  'in_progress',
  'taking_over',
  'taken_over',
  'ended_by_ai',
  'transferred_to_human',
  'no_answer',
  'failed',
];

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asObject = (value: unknown): JsonObject => (isObject(value) ? value : {});
const asString = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);
const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const parseRows = (value: unknown): AiCallSummaryRow[] =>
  (Array.isArray(value) ? value : []).flatMap((row) => {
    const { key, label, value: rowValue } = asObject(row);
    const text = asString(rowValue);
    if (!text) return [];
    return [{ key: asString(key) ?? '', label: asString(label) ?? asString(key) ?? '', value: text }];
  });

/**
 * Reads the card from a message's `metadata`. It is typed as JSON by the API, so every field is
 * checked here instead of trusted; returns null when the payload is not a card.
 */
export function parseAiCallSummaryCard(metadata: unknown): AiCallSummaryCard | null {
  if (!isObject(metadata)) return null;

  const status = STATUSES.find((value) => value === metadata.status);
  if (!status) return null;

  const direction: AiCallSummaryDirection = metadata.direction === 'outgoing' ? 'outgoing' : 'incoming';
  const aiAgent = asObject(metadata.aiAgent);
  const aiAgentId = asNumber(aiAgent.id);
  const contact = asObject(metadata.contact);
  const recording = asObject(metadata.recording);
  const recordingUrl = asString(recording.url);
  const summary = asObject(metadata.summary);
  const takenOverBy = asObject(metadata.takenOverBy);
  const takenOverByName = asString(takenOverBy.name);

  return {
    status,
    direction,
    aiAgent: aiAgentId ? { id: aiAgentId, name: asString(aiAgent.name) ?? '' } : null,
    sessionId: asNumber(metadata.sessionId),
    messageConversationId: asNumber(metadata.messageConversationId),
    channelId: asNumber(metadata.channelId),
    clientId: asNumber(metadata.clientId),
    contact: {
      name: asString(contact.name),
      phoneNumber: asString(contact.phoneNumber),
      flag: asString(asObject(contact.country).flag),
    },
    startedAt: asString(metadata.startedAt),
    durationSeconds: asNumber(metadata.durationSeconds),
    canTakeOver: status === 'in_progress' && metadata.canTakeOver === true,
    takenOverBy: takenOverByName
      ? { userWorkspaceId: asNumber(takenOverBy.userWorkspaceId), name: takenOverByName }
      : null,
    recording: recordingUrl ? { url: recordingUrl, durationSeconds: asNumber(recording.durationSeconds) } : null,
    summary: {
      text: asString(summary.text) ?? '',
      rows: parseRows(summary.rows),
    },
  };
}

export const formatCallDuration = (seconds: number) => {
  const duration = dayjs.duration(seconds, 'seconds');
  return duration.hours() > 0 ? duration.format('HH:mm:ss') : duration.format('mm:ss');
};
